/**
 * Tabla de detalle del pipeline por participante. Compartida por MonitoreoTab y por la pestaña
 * de Trazabilidad: las nueve columnas son las mismas y solo cambia lo que se pinta en la de
 * muestreo, que cada pantalla aporta con renderSampleCell.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import './ParticipantDetailTable.css';

export interface ParticipantDetailRow {
  userId: string;
  displayName?: string | null;
  email?: string | null;
  ouId?: string | null;
  timezone: string;
  surveysToday: number;
  surveysThisWeek: number;
  hoursSinceLastEligibleMeeting?: number | null;
  isEligible: boolean;
  reason?: string | null;
  includedInSample: boolean;
  samplingReason?: string | null;
}

interface ParticipantDetailTableProps {
  rows: ParticipantDetailRow[];
  highlightUserId?: string | null;
  renderSampleCell?: (row: ParticipantDetailRow) => ReactNode;
}

function formatHours(hours?: number | null): string {
  if (hours === null || hours === undefined) return '—';
  return hours < 1 ? `${Math.round(hours * 60)}min` : `${hours.toFixed(1)}h`;
}

function shortId(identifier: string): string {
  return identifier.length > 12 ? `${identifier.slice(0, 8)}…` : identifier;
}

function sampleRowClass(row: ParticipantDetailRow): string {
  if (row.includedInSample) return 'pdt-row--sampled';
  return row.isEligible ? 'pdt-row--eligible' : 'pdt-row--skip';
}

export function ParticipantDetailTable({
  rows, highlightUserId, renderSampleCell,
}: ParticipantDetailTableProps) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <div className="pdt-empty">{t('monitor.noParticipants')}</div>;
  }

  function renderSampleCellContent(row: ParticipantDetailRow): ReactNode {
    if (renderSampleCell) {
      return renderSampleCell(row);
    }
    if (!row.includedInSample) {
      return '—';
    }
    return <span className="pdt-badge pdt-badge--sampled">{t('monitor.sampled')}</span>;
  }

  return (
    <div className="pdt-wrap">
      <table className="pdt-table">
        <thead>
          <tr>
            <th>{t('monitor.colUserId')}</th>
            <th>{t('monitor.colOu')}</th>
            <th>{t('monitor.colTimezone')}</th>
            <th>{t('monitor.colToday')}</th>
            <th>{t('monitor.colWeek')}</th>
            <th>{t('monitor.colInterval')}</th>
            <th>{t('monitor.colStatus')}</th>
            <th>{t('monitor.colReason')}</th>
            <th>{t('monitor.colSample')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.userId}
              className={[
                'pdt-row',
                sampleRowClass(row),
                row.userId === highlightUserId ? 'pdt-row--highlight' : '',
              ].filter(Boolean).join(' ')}
            >
              <td data-label={t('monitor.colUserId')} title={row.userId}>
                <span className={row.displayName ? undefined : 'pdt-user-id'}>
                  {row.displayName ?? shortId(row.userId)}
                </span>
                {row.email && <span className="pdt-email">{row.email}</span>}
              </td>
              <td data-label={t('monitor.colOu')}>{row.ouId ?? '—'}</td>
              <td data-label={t('monitor.colTimezone')} className="pdt-tz">{row.timezone}</td>
              <td data-label={t('monitor.colToday')} className="pdt-num">{row.surveysToday}</td>
              <td data-label={t('monitor.colWeek')} className="pdt-num">{row.surveysThisWeek}</td>
              <td data-label={t('monitor.colInterval')} className="pdt-num">
                {formatHours(row.hoursSinceLastEligibleMeeting)}
              </td>
              <td data-label={t('monitor.colStatus')}>
                {row.isEligible
                  ? <span className="pdt-badge pdt-badge--ok">
                      <i className="fas fa-check" aria-hidden="true" /> {t('monitor.eligible')}
                    </span>
                  : <span className="pdt-badge pdt-badge--skip" title={row.reason ?? undefined}>
                      <i className="fas fa-times" aria-hidden="true" /> {t('monitor.excluded')}
                    </span>}
              </td>
              <td data-label={t('monitor.colReason')} className="pdt-reason">
                {row.reason ?? row.samplingReason ?? '—'}
              </td>
              <td data-label={t('monitor.colSample')}>
                {renderSampleCellContent(row)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
